import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Search, 
  ArrowLeft, 
  RotateCcw, 
  Globe, 
  CheckCircle2, 
  ExternalLink, 
  Plus, 
  Trash2,
  HelpCircle,
  FileText
} from 'lucide-react';

export const GoogleIndexChecker = () => {
  const { withAd, toast } = useAdRedirect();
  const [urlsInput, setUrlsInput] = useState(`https://toolmetrico.com\nhttps://toolmetrico.com/image-compressor\nhttps://toolmetrico.com/qrcode-generator\nhttps://toolmetrico.com/about`);
  const [urlsList, setUrlsList] = useState([
    { url: 'https://toolmetrico.com', status: 'checked' },
    { url: 'https://toolmetrico.com/image-compressor', status: 'checked' },
    { url: 'https://toolmetrico.com/qrcode-generator', status: 'checked' },
    { url: 'https://toolmetrico.com/about', status: 'checked' }
  ]);

  const processUrls = (e) => {
    if (e) e.preventDefault();
    const raw = urlsInput.split('\n').map(u => u.trim()).filter(Boolean);
    const formatted = raw.map(url => {
      let clean = url;
      if (!clean.startsWith('http://') && !clean.startsWith('https://')) {
        clean = 'https://' + clean;
      }
      return { url: clean, status: 'checked' };
    });
    setUrlsList(formatted);
  };

  const handleReset = () => {
    setUrlsInput('');
    setUrlsList([]);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Google Index Checker & SERP Operator" 
        description="Verify URL indexing status with instant Google search operators. Bulk check webpage search console discoverability client-side."
        keywords="google index checker, check indexing status, google search operator, site index check, bulk url index tool, serp verification"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Google Index Checker</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Google Index Checker & SERP Operator
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Verify live indexing status across Google Search Console by executing direct &quot;site:&quot; search verification operators instantly.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Input Box */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} style={{ color: 'var(--primary)' }} /> Bulk URLs list (One per line)
              </h3>
              <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <RotateCcw size={14} /> Clear List
              </button>
            </div>

            <form onSubmit={processUrls}>
              <textarea 
                rows="10"
                value={urlsInput} 
                onChange={(e) => setUrlsInput(e.target.value)}
                placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                style={{ width: '100%', padding: '16px', borderRadius: '14px', border: '1px solid var(--border-color)', fontSize: '0.9rem', outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6, marginBottom: '20px' }}
              />

              <button 
                type="submit" 
                style={{ width: '100%', background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '14px', borderRadius: '12px', fontSize: '0.95rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'var(--transition-smooth)' }}
              >
                <Search size={18} /> Parse & Generate Verification Links
              </button>
            </form>
          </div>

        </div>

        {/* Right Side: Verification Operators */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe size={18} style={{ color: '#10b981' }} /> Live Index Verification Links
              </h3>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total URLs: {urlsList.length}</span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {urlsList.length > 0 ? urlsList.map((item, idx) => {
                const siteOperatorUrl = `https://www.google.com/search?q=site:${encodeURIComponent(item.url)}`;
                return (
                  <div key={idx} style={{ background: 'var(--bg-secondary)', padding: '14px 16px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', maxWidth: '100%', overflow: 'hidden' }}>
                      <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {item.url}
                      </span>
                    </div>

                    <a 
                      href={siteOperatorUrl}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ background: '#10b981', color: '#ffffff', textDecoration: 'none', padding: '8px 14px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'var(--transition-smooth)' }}
                    >
                      Check Index <ExternalLink size={14} />
                    </a>
                  </div>
                );
              }) : (
                <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  Paste a list of URLs and click parse above to load search verification operators.
                </div>
              )}
            </div>

            <AdToast message={toast} />
          </div>

          <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ color: 'var(--primary)', padding: '4px' }}>
              <HelpCircle size={24} />
            </div>
            <div>
              <h4 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 6px 0' }}>How to Verify</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                Clicking <strong>Check Index</strong> opens a new Google Search tab with the <code>site:</code> operator. If your webpage appears in the search results, it is fully indexed and discoverable by Googlebot. If no results appear, submit your sitemap via Google Search Console.
              </p>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="google-index-checker" />
    </div>
  );
};

export default GoogleIndexChecker;
